import { NgOptimizedImage } from "@angular/common";
import { Component, EventEmitter, Input, Output, inject } from "@angular/core";
import { MatDialog } from "@angular/material/dialog";
import { Router } from "@angular/router";
import { toPascalCase } from "@core/services/utils/functions/toPascalCase";
import { processHttpError } from "@core/services/utils/process-http-error";
import { TranslatePipe } from "@ngx-translate/core";
import {
  HumanMessageRequest,
  ParticipantTypeEnum,
} from "@services/ui-helpers/_models/interfaces/messaging.interfaces";
import { MessagingService } from "@services/ui-helpers/messaging-services/messaging.service";
import { getParticipantImagePath } from "@services/ui-helpers/participant-image.util";
import { MessageDialogComponent } from "@ui/components/messaging/message-dialog/message-dialog.component";
import { IconDisplayComponent } from "@ui/ui-helpers/display-formats/icon-display/icon-display.component";

@Component({
  selector: "app-contact-card",
  templateUrl: "./contact-card.component.html",
  styleUrl: "./contact-card.component.scss",
  imports: [TranslatePipe, IconDisplayComponent, NgOptimizedImage],
})
export class ContactCardComponent {
  private router = inject(Router);
  private messagingService = inject(MessagingService);
  private dialog = inject(MatDialog);

  @Input() public fullName = "";
  @Input() public userId: string | undefined;
  @Input() public participantType!: ParticipantTypeEnum;
  @Input() public profileImageUrl?: string;
  @Input() public hasAnnotations = false;
  @Output() public annotationsClick = new EventEmitter<void>();

  protected get type(): string {
    return `ParticipantType.${toPascalCase(this.participantType)}`;
  }

  protected get imageSource(): string {
    return (
      this.profileImageUrl ?? getParticipantImagePath(this.participantType)
    );
  }

  onContactClick(): void {
    if (!this.userId) {
      return;
    }

    const dialogRef = this.dialog.open(MessageDialogComponent, {
      data: {
        participantId: this.userId,
      },
    });

    dialogRef.afterClosed().subscribe({
      next: (result: HumanMessageRequest) => {
        this.submitMessage(result);
      },
    });
  }

  onAnnotationsClick(): void {
    this.annotationsClick.emit();
  }

  private async submitMessage(
    initialMessage: HumanMessageRequest
  ): Promise<void> {
    try {
      await this.messagingService.sendHumanMessage(initialMessage);

      // When a thread is created/used, open the messaging page directly on
      // that conversation so the user sees the message they just sent.
      const threadId =
        await this.messagingService.findThreadIdByReceiverParticipant(
          initialMessage.receiverParticipantId
        );

      await this.router.navigate(["/messaging"], {
        queryParams: threadId ? { threadId } : undefined,
      });
    } catch (error) {
      processHttpError(error);
    }
  }
}
